import {
	Card,
	Divider,
	Tabs,
	Modal,
	Button,
	Input,
	Select,
	Progress,
	InputNumber,
} from 'antd';
import {
	Chart as Chartjs,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import NativeNumber from '../../component/Numeral';
import {
	investmentTopUpList,
	investmentTransactionStatements,
	retrieveAdminInvestmentItems,
	registerInvestment,
	topUpInvestment,
	startWithdrawal,
	startProxyWithdrawal,
	validateProxyWithdrawal,
	resetProxyWithdrawal,
} from '../../methods/redux/reducers/investment';
import remakeQueryString from '../../methods/utils/remake-query-string';
import { getAdminWalletBalance } from '../../methods/redux/reducers/general';
import InvestmentTransactionStatements from './InvestmentTransactionStatement';
import InvestmentTopUpItems from './InvestmentTopUpItems';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import InvestmentWithdrawalList from './InvestmentWithdrawalList';
// import { getUserWalletBalance } from '../../methods/redux/reducers/user-profile';

const { TabPane } = Tabs;

Chartjs.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const SingleInvestmentDetailsStyle = styled.div`
	& .locked-indicator {
		padding: 3px;
		color: white;
		text-align: center;
		font-size: 12px;
		font-weight: bold;
		background: red;
	}

	& .iscollateral-indicator {
		position: relative;
		top: -25px;
		padding: 4px;
		margin: auto;
		color: white;
		text-align: center;
		font-size: 13.5px;
		font-weight: bold;
		background: orange;
		width: 75%;
	}

	& .title {
		text-transform: capitalize;
	}

	& .profile-details {
		& > div {
			display: inline-block;
			padding: 5px;
			margin-right: 5px;

			& .label {
				font-size: 12px;
				color: gray;
			}
		}
	}
`;

const SingleInvestment = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [visible, setVisible] = useState(false);
	const [visibleTopUp, setVisibleTopUp] = useState(false);
	const [buttonVisible, setButtonVisible] = useState(false);
	const [withdrawModal, setWithdrawModal] = useState(false);
	const [loadingRetry, setLoadingRetry] = useState(false);
	const [loadingBalance, setLoadingBalance] = useState(false);
	const [totalUSDInWallet, setTotalUSDInWallet] = useState(0);
	const [investmentState, setInvestmentState] = useState<any>({
		percentComplete: 0,
		daysLeft: 0,
	});
	const {
		adminInvestmentItems,
		investmentStatements,
		investmentTopUps,
		withdrawalValidate,
	} = useAppSelector((store) => store.investment);
	// const { user, walletBalance } = useAppSelector((store) => store.userProfile);
	const { cryptoBalances, fiatBalances } = useAppSelector(
		(store) => store.general
	);

	const searchParamsObject = Object.fromEntries(searchParams);
	interface Investment {
		_id: any;
		title: string;
		investmentForm: string;
		durationInMonth: number;
		investmentStatus: string;
		reInvestAfterMaturity: string;
		riseInvestmentId: string;
		status: string;
		totalDeposit: number;
		totalProfit: number;
		currencyId: any;
		totalWithdrawn: number;
		createdAt: string;
		updatedAt: string;
		completionDate: string;
		riseVestData: any;
		userId: any;
	}
	const investment = adminInvestmentItems.investment as Investment;
	const [state, setState] = useState({
		// bvn: '',
		// memberId: '',
		investmentId: searchParamsObject.investmentId,
	});
	const [topUpState, setTopUpState] = useState({
		amount: 0,
		memberId: '',
		investmentId: searchParamsObject.investmentId,
	});
	const [withdrawState, setWithdrawState] = useState({
		amount: 0,
		investmentId: searchParamsObject.investmentId,
	});
	const [validateWithdrawState, setValidateWithdrawState] = useState({
		liquidate: false,
		amount: 0,
		investmentId: searchParamsObject?.investmentId,
		memberId: '',
	});

	// const submit = () => {
	// 	dispatch(startProxyInvestment({ ...state }));
	// 	setVisible(false);
	// };
	const submitTopUpInvestment = () => {
		dispatch(topUpInvestment({ ...topUpState }));
		setVisibleTopUp(false);
	};

	const getBalances = async () => {
		setLoadingBalance(true);
		if (investment?.userId?._id && investment?.userId?._id.length > 0) {
			try {
				await dispatch(getAdminWalletBalance(investment.userId?._id));
				setLoadingBalance(false);
			} catch (error) {
				setLoadingBalance(false);
			}
		}
	};

	useEffect(() => {
		if (investment?.userId?._id && investment?.userId?._id.length > 0) {
			getBalances();
		}
	}, [investment?.userId?._id]);

	const balances = cryptoBalances.concat(fiatBalances);

	useEffect(() => {
		const total = balances
			.map(
				(item: any) =>
					Number(item.balance || 0) * Number(item.currencyId?.usdValue || 0)
			)
			.reduce((prev, current) => prev + current, 0);
		setTotalUSDInWallet(total);
	}, [balances]);

	useEffect(() => {
		if (investment?.userId?._id) {
			setTopUpState((prevState) => ({
				...prevState,
				memberId: investment.userId._id, // Now it's set dynamically
			}));
			setValidateWithdrawState((initialState) => ({
				...initialState,
				memberId: investment.userId._id,
			}));
		}
	}, [investment]);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		const updateQueryString = (query: any, action = 'update') => {
			remakeQueryString(query, searchParams, setSearchParams, action);
		};
		updateQueryString({ investmentId: searchParamsObject.investmentId });
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAdminInvestmentItems({ ...queryData }));
		}
		dispatch(
			investmentTransactionStatements({
				associatedInvestmentId: searchParamsObject.investmentId,
			})
		);
		dispatch(
			investmentTopUpList({ investmentId: searchParamsObject.investmentId })
		);
		dispatch(getCurrencies({}));

		// dispatch(getUserWalletBalance(investment?.userId?._id));
	}, []);
	useEffect(() => {
		if (adminInvestmentItems.investment && !investment?.riseInvestmentId) {
			setButtonVisible(true);
		} else {
			setButtonVisible(false);
		}
	}, [adminInvestmentItems]); // ✅ Depend on `investmentItems`

	useEffect(() => {
		if (investment) {
			const { durationInMonth, createdAt } = investment;
			let daysLeft = 0;
			let percentComplete = 100;

			// Calculate days difference
			if (durationInMonth && createdAt) {
				// Parse the start date
				const startDate: any = new Date(createdAt);

				// Calculate end date by adding months
				const endDate: any = new Date(startDate);
				endDate.setMonth(startDate.getMonth() + durationInMonth);
				const totalDurationInDays =
					// moment(completionDate).diff(moment(createdAt), 'days') + 1;
					moment(endDate).diff(moment(startDate), 'days') + 1;

				if (moment().isBefore(endDate)) {
					daysLeft = moment(endDate).diff(moment(), 'days') + 1;
					percentComplete =
						((Number(totalDurationInDays) - daysLeft) /
							Number(totalDurationInDays)) *
						100;
					percentComplete = Number(percentComplete.toFixed(2));
				}

				setInvestmentState({ ...investmentState, daysLeft, percentComplete });
			}
		}
	}, [investment]);

	const liquidateEnum = [
		{
			key: 'true',
			label: 'true',
		},
		{
			key: 'false',
			label: 'false',
		},
	];
	const [withdrawalVisible, setWithdrawalVisible] = useState(false);

	const submitWithdrawal = () => {
		if (withdrawalValidate && withdrawalValidate?.validationCode) {
			dispatch(
				startProxyWithdrawal({
					...validateWithdrawState,
					validationCode: withdrawalValidate?.validationCode,
				})
			);
		}

		setWithdrawalVisible(false);
	};

	const validateWithdraw = async () => {
		await dispatch(validateProxyWithdrawal({ ...validateWithdrawState }));
	};

	const cancelProxyWithdraw = async () => {
		setValidateWithdrawState({
			...validateWithdrawState,
			amount: 0,
		});
		await dispatch(resetProxyWithdrawal());
		setWithdrawalVisible(false);
	};

	const startThirdPartyWithdraw = async () => {
		dispatch(startWithdrawal({ ...withdrawState }));
	};

	const retryInvestment = async () => {
		setLoadingRetry(true);
		await dispatch(registerInvestment({ ...state }));
		setLoadingRetry(false);
		setState({
			// bvn: '', memberId: '',
			investmentId: '',
		});
		setVisible(false);
		window.location.reload();
	};

	return (
		<SingleInvestmentDetailsStyle>
			<Card style={{ position: 'relative' }}>
				<Button
					onClick={() => window.history.back()}
					style={{ padding: '10px', cursor: 'pointer' }}
				>
					Back
				</Button>

				{/*  */}
				{adminInvestmentItems.investment && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'flex-end',
							gap: '10px',
						}}
					>
						<Button type="primary" onClick={() => setWithdrawModal(true)}>
							Withdraw From Third Party
						</Button>
					</div>
				)}
				{/* */}
				<div style={{ display: 'flex', gap: '4px' }}>
					<h1 className="title">
						{adminInvestmentItems.investment ? investment?.title : 'No Title'}{' '}
						for
					</h1>
					{investment?.userId && (
						<Link to={`/user-profile/${investment?.userId?._id}`}>
							<h1 className="title" style={{ color: '#1890ff' }}>
								{investment?.userId?.username ? (
									<span>@{investment?.userId?.username}</span>
								) : (
									investment.userId?.email
								)}
							</h1>
						</Link>
					)}
				</div>
				<p className="label">Title</p>
				<div className="profile-details">
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.investmentForm
								: 'No InvestmentForm'}
						</p>
						<p className="label">Investment Form</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.riseInvestmentId
								: 'No riseInvestmentId'}{' '}
						</p>
						<p className="label">Rise vest Investment ID</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.investmentStatus
								: 'No investmentStatus'}
						</p>
						<p className="label">Investment Status</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.status
								: 'No Status'}
						</p>
						<p className="label">Status</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.riseVestData?.planType
								: 'No Plan Type'}
						</p>
						<p className="label">PlanType</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.riseVestData?.assetType
								: 'No Asset Type'}
						</p>
						<p className="label">Asset Type</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.riseVestData?.category
								: 'No Category'}
						</p>
						<p className="label">Category</p>
					</div>
					<div>
						<p>
							<strong>
								{adminInvestmentItems.investment
									? investment?.riseVestData?.currentBalance
									: 'No currentBalance'}{' '}
								{investment?.currencyId?.symbol}
							</strong>
						</p>
						<p className="label">Current Balance</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? investment?.riseVestData?.tag
								: 'No tag'}
						</p>
						<p className="label">Tag</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment &&
							investment?.reInvestAfterMaturity
								? 'True'
								: 'False'}
						</p>
						<p className="label">ReInvest After Maturity</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										adminInvestmentItems.investment
											? investment?.riseVestData?.goalTimeline
											: 'No goalTimeline'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Validity (month)</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										adminInvestmentItems.investment
											? investment?.riseVestData?.returnPercent
											: 'No returnPercent'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Return Percent</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										adminInvestmentItems.investment
											? investment?.totalDeposit
											: 'No totalDeposit'
									}
									decimalPlaces={4}
								/>{' '}
								{investment?.currencyId?.symbol}
							</strong>
						</p>
						<p className="label">Total Deposit</p>
					</div>
					<div>
						<p style={{ color: 'var(--green-color)' }}>
							<strong>
								<NativeNumber
									value={
										adminInvestmentItems.investment
											? investment?.totalProfit
											: 'No totalDeposit'
									}
									decimalPlaces={4}
								/>{' '}
								{investment?.currencyId?.symbol}
							</strong>
						</p>
						<p className="label">Total Profit</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									decimalPlaces={4}
									value={
										adminInvestmentItems.investment
											? investment?.totalWithdrawn
											: 'No totalWithdrawn'
									}
								/>{' '}
								{investment?.currencyId?.symbol}
							</strong>
						</p>
						<p className="label">Total Withdrawn</p>
					</div>
					<div>
						<p>
							<strong>
								<NativeNumber
									value={
										adminInvestmentItems.investment
											? investment?.durationInMonth
											: 'No durationInMonth'
									}
									decimalPlaces={4}
								/>{' '}
							</strong>
						</p>
						<p className="label">Duration In Months</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investment
								? moment(
										adminInvestmentItems.investment
											? investment?.createdAt
											: 'No createdAt'
								  ).format('MMM DD, YY / hh:mm:ss a')
								: '--'}
						</p>
						<p className="label">Created Date</p>
					</div>
					<div>
						<p>
							{moment(
								adminInvestmentItems.investment
									? investment?.updatedAt
									: 'No updatedAt'
							).format('MMM DD, YY / hh:mm:ss a')}
						</p>
						<p className="label">Updated Date</p>
					</div>
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'flex-start',
						justifySelf: 'flex-start',
						gap: '10px',
					}}
				>
					<Button
						style={{ marginLeft: 'auto' }}
						type="primary"
						onClick={() => setWithdrawalVisible(true)}
					>
						Proxy Withdraw
					</Button>

					<Button type="primary" onClick={() => setVisibleTopUp(true)}>
						Proxy TopUp Investment
					</Button>
					{buttonVisible === true ? (
						<Button type="primary" onClick={() => setVisible(true)}>
							Retry Investment
						</Button>
					) : null}
				</div>
				<Divider />
				<h2 className="label">Investment Profile</h2>

				{adminInvestmentItems?.investment &&
					investment?.investmentForm === 'STOCKS' && (
						<div style={{ marginBottom: '12px' }}>
							<p>
								Running for{' '}
								<strong>{investmentState.daysLeft} days left</strong>.
							</p>
						</div>
					)}

				<div
					style={{
						display:
							adminInvestmentItems?.investment &&
							investment?.investmentForm === 'REAL_ESTATE'
								? 'flex'
								: 'block',
						gap: '24px',
					}}
				>
					{adminInvestmentItems?.investment &&
						investment?.investmentForm === 'REAL_ESTATE' && (
							<div style={{ marginBottom: '12px' }}>
								<Progress
									type="circle"
									percent={investmentState.percentComplete}
								/>
								<p>Percent completion. </p>
							</div>
						)}

					<div className="profile-details">
						<div>
							<p>
								{adminInvestmentItems.investmentProfile?.active
									? 'True'
									: 'False'}
							</p>
							<p className="label">Active</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.assetType
									: '--'}
							</p>
							<p className="label">Asset Type</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.category
									: '--'}
							</p>
							<p className="label">Category</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.currentBalance
										: '--'}
								</strong>
							</p>
							<p className="label">Current Balance</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.customer
									: '--'}
							</p>
							<p className="label">Customer</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.duration
										: '--'}
								</strong>
							</p>
							<p className="label">Duration</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.frequency
									: '--'}
							</p>
							<p className="label">Frequency</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.goalTimeline
										: '--'}
								</strong>
							</p>
							<p className="label">Goal Timeline</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.investmentId
									: '--'}
							</p>
							<p className="label">Investment Id</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile?.isArchived
									? 'True'
									: 'False'}
							</p>
							<p className="label">Is Archived</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile?.isClosed
									? 'True'
									: 'False'}
							</p>
							<p className="label">Is Closed</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile?.isRecurring
									? 'True'
									: 'False'}
							</p>
							<p className="label">Is Recurring</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.maturityDate
									: '--'}
							</p>
							<p className="label">Maturity Date</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.tag
									: '--'}
							</p>
							<p className="label">Tag</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.totalDeposited
										: '--'}
								</strong>
							</p>
							<p className="label">Total Deposited</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.totalFunded
										: '--'}
								</strong>
							</p>
							<p className="label">Total Funded</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.totalReturns
										: '--'}
								</strong>
							</p>
							<p className="label">Total Returns</p>
						</div>
						<div>
							<p>
								<strong>
									{adminInvestmentItems.investmentProfile
										? adminInvestmentItems.investmentProfile?.totalWithdrawn
										: '--'}
								</strong>
							</p>
							<p className="label">Total Withdrawn</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? adminInvestmentItems.investmentProfile?.type
									: '--'}
							</p>
							<p className="label">Type</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? moment(
											adminInvestmentItems.investmentProfile?.createdAt
									  ).format('MMM DD, YY / hh:mm:ss a')
									: '--'}
							</p>
							<p className="label">Created At</p>
						</div>
						<div>
							<p>
								{adminInvestmentItems.investmentProfile
									? moment(
											adminInvestmentItems.investmentProfile?.updatedAt
									  ).format('MMM DD, YY / hh:mm:ss a')
									: '--'}
							</p>
							<p className="label">Updated At</p>
						</div>
					</div>
				</div>

				<Divider />
				<h2 className="label">Investment Returns</h2>
				<div className="profile-details">
					<div>
						<p>
							{adminInvestmentItems.investmentReturns
								? adminInvestmentItems.investmentReturns?.customer
								: '--'}
						</p>
						<p className="label">Customer</p>
					</div>
					<div>
						<p>
							{adminInvestmentItems.investmentReturns
								? adminInvestmentItems.investmentReturns?.investmentId
								: '--'}
						</p>
						<p className="label">Investment Id</p>
					</div>
					<div>
						<p>
							<strong>
								{adminInvestmentItems.investmentReturns
									? adminInvestmentItems.investmentReturns?.returnPercent
									: '--'}
							</strong>
						</p>
						<p className="label">Return Percent</p>
					</div>
					<div>
						<p>
							<strong>
								{adminInvestmentItems.investmentReturns
									? adminInvestmentItems.investmentReturns?.totalDeposited
									: '--'}
							</strong>
						</p>
						<p className="label">Total Deposited</p>
					</div>
					<div>
						<p>
							<strong>
								{adminInvestmentItems.investmentReturns
									? adminInvestmentItems.investmentReturns?.totalReturns
									: '--'}
							</strong>
						</p>
						<p className="label">Total Returns</p>
					</div>
					<div>
						<p>
							<strong>
								{adminInvestmentItems.investmentReturns
									? adminInvestmentItems.investmentReturns?.totalWithdrawn
									: '--'}
							</strong>
						</p>
						<p className="label">Total Withdrawn</p>
					</div>
				</div>
				<Divider />
				<Tabs defaultActiveKey="1">
					<TabPane tab="Transaction Statements" key="1">
						{investmentStatements.itemsList.map((transaction: any) => (
							<InvestmentTransactionStatements
								key={transaction._id}
								transaction={transaction}
							/>
						))}
					</TabPane>
					<TabPane tab="Investment TopUp" key="2">
						<div style={{ marginTop: '20px' }}>
							<Link
								title="Investment Topups"
								className="out-links"
								to={`/core-modules/investment/top-ups?investmentId=${investment?._id}`}
							>
								View all
							</Link>
						</div>
						{investmentTopUps?.topUps?.itemsList?.map((item: any) => (
							<InvestmentTopUpItems key={item._id} item={item} />
						))}
						{/* {investmentItems?.topUps?.map((item: any) => (
							<InvestmentTopUpItems key={item._id} item={item} />
						))} */}
					</TabPane>
					<TabPane tab="Withdrawals" key="3">
						<Link
							title="Investment Topups"
							className="out-links"
							to={`/core-modules/investment/withdrawals?investmentId=${investment?._id}`}
						>
							View all
						</Link>
						{adminInvestmentItems?.withdrawals?.map((item: any) => (
							<InvestmentWithdrawalList key={item._id} list={item} />
						))}
					</TabPane>
				</Tabs>
			</Card>

			<Modal
				okText="Start Withdrawal"
				title="Withdraw From Third Party"
				onOk={startThirdPartyWithdraw}
				open={withdrawModal} // ✅ Fix deprecated `visible` prop
				onCancel={() => setWithdrawModal(false)}
			>
				<p>Amount</p>
				<InputNumber
					style={{ width: '100%' }}
					value={withdrawState.amount}
					onChange={(value: number | null) =>
						setWithdrawState({
							...withdrawState,
							amount: value ?? 0,
						})
					}
				/>
			</Modal>
			{/* <Modal
				okText="Start Investment"
				title="Start Investment"
				onOk={submit}
				open={visible} // ✅ Fix deprecated `visible` prop
				onCancel={() => setVisible(false)}
			>
				<p>Bvn Number</p>
				<Input
					value={state.bvn}
					onChange={(e) => setState({ ...state, bvn: e.target.value })}
				/>
			</Modal> */}
			<Modal
				okText="TopUp Investment"
				title="TopUp Investment"
				onOk={submitTopUpInvestment}
				visible={visibleTopUp}
				onCancel={() => setVisibleTopUp(false)}
			>
				{investment?.userId && (
					<p>
						This will deduct the amount from{' '}
						<strong>
							{investment?.userId?.username
								? `@${investment?.userId?.username}`
								: investment.userId?.email}
						</strong>{' '}
						wallet to top up his investment
					</p>
				)}
				<br />
				<p>User Wallet Balance:</p>
				<h2>
					{loadingBalance ? (
						'Loading...'
					) : (
						<strong>
							$<NativeNumber value={totalUSDInWallet} decimalPlaces={4} />
						</strong>
					)}
				</h2>
				<br />
				<p>Amount</p>
				<InputNumber
					type="number"
					value={topUpState.amount}
					style={{ width: '100%' }}
					onChange={(value: number | null) =>
						setTopUpState({ ...topUpState, amount: value ?? 0 })
					}
				/>
			</Modal>
			<Modal
				okText={
					withdrawalValidate && withdrawalValidate.allowedWithdrawal === true
						? 'Process Withdrawal'
						: 'Validate Withdrawal'
				}
				title={
					investment?.userId
						? `Create investment withdrawal on behalf of ${
								investment?.userId?.username
									? `@${investment?.userId?.username}`
									: investment.userId?.email
						  }`
						: 'Create investment withdrawal on behalf of user'
				}
				onOk={
					withdrawalValidate && withdrawalValidate.allowedWithdrawal === true
						? submitWithdrawal
						: validateWithdraw
				}
				visible={withdrawalVisible}
				onCancel={() => cancelProxyWithdraw()}
			>
				{withdrawalValidate &&
				withdrawalValidate.allowedWithdrawal === true ? null : (
					<>
						<p>Amount</p>
						<Input
							type="number"
							value={validateWithdrawState.amount}
							onChange={(e: any) =>
								setValidateWithdrawState({
									...validateWithdrawState,
									amount: e.target.value,
								})
							}
						/>
						<p>Liquidate</p>
						<Select
							defaultValue={validateWithdrawState.liquidate}
							style={{ width: '100%' }}
							onChange={(value) => {
								setValidateWithdrawState({
									...validateWithdrawState,
									liquidate: value,
								});
							}}
						>
							{liquidateEnum.map((i: any) => (
								<Select.Option key={i.key} value={i.key}>
									{i.label}
								</Select.Option>
							))}
						</Select>
					</>
				)}
				{withdrawalValidate && withdrawalValidate.investmentStatus !== '' ? (
					<>
						<strong>Withdrawal Validation record:</strong>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false ||
									withdrawalValidate.amount >
										withdrawalValidate.maxWithdrawableAmount
										? 'red'
										: 'green',
							}}
						>
							Allow-withdrawal:{' '}
							{withdrawalValidate.allowedWithdrawal === false
								? 'false'
								: 'true'}
						</p>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false
										? 'red'
										: 'green',
							}}
						>
							Amount: {withdrawalValidate.amount}
						</p>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false
										? 'red'
										: 'green',
							}}
						>
							Credit-amount: {withdrawalValidate.creditAmount}
						</p>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false
										? 'red'
										: 'green',
							}}
						>
							Investment-status: {withdrawalValidate.investmentStatus}
						</p>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false
										? 'red'
										: 'green',
							}}
						>
							Transaction-fee: {withdrawalValidate.transactionFee}
						</p>
						<p
							style={{
								color:
									withdrawalValidate.allowedWithdrawal === false
										? 'red'
										: 'green',
							}}
						>
							Max-withdrawable-amount:{' '}
							{withdrawalValidate.maxWithdrawableAmount}
						</p>
					</>
				) : null}
			</Modal>
			{searchParamsObject.investmentId && (
				<Modal
					okText={loadingRetry ? 'Retrying...' : 'Confirm'}
					title="Retry Investment"
					onOk={retryInvestment}
					visible={visible}
					onCancel={() => setVisible(false)}
				>
					<p>Are you sure you want to retry this investment?</p>
				</Modal>
			)}
		</SingleInvestmentDetailsStyle>
	);
};

export default SingleInvestment;
